import { useState } from "react"

import { LockClosedIcon } from "@heroicons/react/20/solid"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { Link, useNavigate } from "react-router-dom"

import { getSolarProposalErrorStatus } from "~/api/hooks/useSolarProposal"
import useSolarProposalConsumption from "~/api/hooks/useSolarProposalConsumption"
import useUpdateSolarProposalConsumption from "~/api/hooks/useUpdateSolarProposalConsumption"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  CONSUMPTION_SAVE_FAILED_MESSAGE,
  CONSUMPTION_SAVED_MESSAGE,
  GREEN_BUTTON_STEP_LINK_LABEL,
  consumptionFormState,
  initialConsumptionDraft,
  type ConsumptionDraft,
} from "./solar-consumption-form"
import { solarProposalPath, solarSetupStepPath } from "./solar-setup-paths"
import { SolarConsumptionCurrentUsage } from "./SolarConsumptionCurrentUsage"
import { SolarConsumptionEditor } from "./SolarConsumptionEditor"
import {
  SolarSummaryStepFooter,
  type SolarSummaryStepMode,
} from "./summary/SolarSummaryStepFooter"

/** A 400 names the bad input; a 409 is the lock's reason; anything else is generic. */
const saveErrorMessage = (error: unknown): string => {
  const status = getSolarProposalErrorStatus(error)
  return status === 400 || status === 409
    ? parseAxiosError(error, CONSUMPTION_SAVE_FAILED_MESSAGE)
    : CONSUMPTION_SAVE_FAILED_MESSAGE
}

/**
 * Consumption: the selected usage, where it came from and whether a manual
 * entry locks it, with Green Button availability as read-only text. A seller
 * enters an annual total or twelve months in kWh. Save writes the database
 * only: savings and the utility baselines read out of date until someone
 * recalculates them, and the offset updates at once. A financing lock makes
 * the step read-only, with the server's reason.
 */
export const SolarConsumptionStep = ({
  proposalId,
  summary,
}: {
  proposalId: string
  /**
   * The Summary's step card: Continue or Save & Continue, and Green Button
   * Data opens over the Summary instead of the Setup rail.
   */
  summary?: SolarSummaryStepMode & { greenButtonPath: string }
}): JSX.Element => {
  const navigate = useNavigate()
  const query = useSolarProposalConsumption(proposalId)
  const update = useUpdateSolarProposalConsumption()
  // Null follows the server's values; a seller's edit makes it theirs.
  const [draft, setDraft] = useState<ConsumptionDraft | null>(null)
  const [confirmingOverride, setConfirmingOverride] = useState(false)
  const [saved, setSaved] = useState(false)

  if (!query.data) {
    return query.isError ? (
      <div
        role="alert"
        className="space-y-3 rounded-xl border border-danger/40 bg-danger/10 p-4"
      >
        <p className="text-sm text-foreground">
          {parseAxiosError(query.error, `Usage could not be loaded.`)}
        </p>
        <button
          type="button"
          className="text-sm font-semibold text-primary underline"
          onClick={() => void query.refetch()}
        >
          Retry
        </button>
      </div>
    ) : (
      <div
        role="status"
        aria-label="Loading usage"
        className="h-40 animate-pulse rounded-xl bg-muted"
      />
    )
  }

  const consumption = query.data
  const locked = consumption.financingLocked
  const current = draft ?? initialConsumptionDraft(consumption.selected)
  // The server's Summary rule: a consumption selection (a migrated profile
  // counts; the legacy columns alone don't) presenting a full year of months.
  const usageCounts =
    consumption.selection != null && consumption.selected?.months.length === 12
  const form = consumptionFormState(current, consumption.selected)

  const edit = (patch: Partial<ConsumptionDraft>): void => {
    setSaved(false)
    setConfirmingOverride(false)
    // An earlier failure no longer describes what the seller is typing.
    update.reset()
    setDraft({ ...current, ...patch })
  }

  const send = (): void => {
    if (!form.body) return
    setConfirmingOverride(false)
    update.mutate(
      { proposalId, body: form.body },
      {
        onSuccess: () => {
          setDraft(null)
          if (summary) {
            summary.onContinue()
            return
          }
          setSaved(true)
        },
      },
    )
  }

  /** Closes setup and brings the savings section, and its Recalculate, into view. */
  const goToSavings = (): void => {
    navigate(solarProposalPath(proposalId))
    requestAnimationFrame(() =>
      document.getElementById(`savings`)?.scrollIntoView({ block: `start` }),
    )
  }

  return (
    <div className="flex flex-col gap-6" data-testid="solar-consumption-step">
      {locked ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-xl border border-border bg-surface p-4 text-sm text-foreground"
        >
          <LockClosedIcon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-muted"
          />
          {consumption.financingLockReason ??
            `Financing locks the household usage.`}
        </p>
      ) : (
        <p className="text-sm text-muted">
          Savings run on this usage. Saving doesn&apos;t recalculate anything.
        </p>
      )}

      <SolarConsumptionCurrentUsage
        consumption={consumption}
        greenButtonStepPath={
          summary?.greenButtonPath ??
          solarSetupStepPath(proposalId, `greenbutton`)
        }
      />

      {summary && !locked ? (
        <p className="text-sm text-muted">
          Have the homeowner&apos;s utility login?{` `}
          <Link
            to={summary.greenButtonPath}
            className="font-semibold text-primary underline"
          >
            {GREEN_BUTTON_STEP_LINK_LABEL}
          </Link>
        </p>
      ) : null}

      {locked ? null : (
        <SolarConsumptionEditor
          draft={current}
          form={form}
          busy={update.isLoading}
          error={update.isError ? saveErrorMessage(update.error) : null}
          confirmingOverride={confirmingOverride}
          onEdit={edit}
          onSave={() =>
            consumption.selected?.source === `green_button`
              ? setConfirmingOverride(true)
              : send()
          }
          onConfirmOverride={send}
          onCancelOverride={() => setConfirmingOverride(false)}
          summary={
            summary && {
              canContinue: usageCounts,
              onContinue: summary.onContinue,
            }
          }
        />
      )}

      {summary && locked ? (
        <SolarSummaryStepFooter
          changed={false}
          busy={false}
          disabled={!usageCounts}
          onSave={summary.onContinue}
          onContinue={summary.onContinue}
        />
      ) : null}

      {saved && draft === null ? (
        <div
          role="status"
          data-testid="solar-consumption-saved"
          className="space-y-2 rounded-xl border border-success/40 bg-success/10 p-4 text-sm text-foreground"
        >
          <p className="flex items-center gap-2 font-semibold text-success">
            <CheckCircleIcon aria-hidden="true" className="size-5" />
            {CONSUMPTION_SAVED_MESSAGE}
          </p>
          <button
            type="button"
            className="font-semibold text-primary underline"
            onClick={goToSavings}
          >
            Go to savings to recalculate
          </button>
        </div>
      ) : null}
    </div>
  )
}
